import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Paperclip, MoreVertical, CheckCheck } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent';
  timestamp: string;
  status?: 'sent' | 'delivered' | 'read';
}
const INITIAL_MESSAGES: Message[] = [{
  id: '1',
  text: 'Hi there! 👋 Welcome to JUMMALL Support.',
  sender: 'agent',
  timestamp: '10:00 AM'
}, {
  id: '2',
  text: 'How can I help you today?',
  sender: 'agent',
  timestamp: '10:00 AM'
}];
export function LiveChatScreen() {
  const {
    goBack
  } = useNavigation();
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = () => {
    if (!inputText.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      status: 'sent'
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputText('');
    setIsTyping(true);
    // Simulate agent typing and responding
    setTimeout(() => {
      setMessages(prev => prev.map(msg => msg.id === newUserMsg.id ? {
        ...msg,
        status: 'read'
      } : msg));
    }, 1000);
    setTimeout(() => {
      setIsTyping(false);
      const newAgentMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: "Thanks for reaching out! I'm looking into that for you right now. Please give me a moment.",
        sender: 'agent',
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setMessages(prev => [...prev, newAgentMsg]);
    }, 2500);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col min-h-screen bg-gray-50" data-id="element-730">
      <Header title="Live Chat" rightElement={<button className="p-2 text-gray-600 hover:text-primary transition-colors" data-id="element-732">
            <MoreVertical className="w-5 h-5" data-id="element-733" />
          </button>} data-id="element-731" />

      {/* Agent Info Banner */}
      <div className="bg-white px-4 py-3 border-b border-gray-100 flex items-center gap-3 sticky top-[53px] z-30 shadow-sm" data-id="element-734">
        <div className="relative" data-id="element-735">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden" data-id="element-736">
            <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=100" alt="Agent" className="w-full h-full object-cover" data-id="element-737" />
          </div>
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-white rounded-full" data-id="element-738"></div>
        </div>
        <div data-id="element-739">
          <h2 className="text-sm font-bold text-gray-900" data-id="element-740">
            Sarah from Support
          </h2>
          <p className="text-xs text-gray-500" data-id="element-741">
            Typically replies in a few minutes
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24" data-id="element-742">
        <div className="text-center mb-6" data-id="element-743">
          <span className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full" data-id="element-744">
            Today
          </span>
        </div>

        <AnimatePresence initial={false} data-id="element-745">
          {messages.map(msg => {
          const isUser = msg.sender === 'user';
          return <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`} data-id="element-746">
                <div className={`max-w-[80%] px-4 py-2.5 shadow-sm ${isUser ? 'bg-primary text-white rounded-2xl rounded-tr-sm' : 'bg-white text-gray-800 rounded-2xl rounded-tl-sm border border-gray-100'}`} data-id="element-747">
                  <p className="text-sm leading-relaxed" data-id="element-748">{msg.text}</p>
                </div>
                <div className="flex items-center gap-1 mt-1 px-1" data-id="element-749">
                  <span className="text-[10px] text-gray-400 font-medium" data-id="element-750">
                    {msg.timestamp}
                  </span>
                  {isUser && msg.status === 'read' && <CheckCheck className="w-3 h-3 text-primary" data-id="element-751" />}
                  {isUser && msg.status === 'sent' && <CheckCheck className="w-3 h-3 text-gray-300" data-id="element-752" />}
                </div>
              </motion.div>;
        })}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-start" data-id="element-753">
            <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm border border-gray-100 shadow-sm flex gap-1" data-id="element-754">
              <motion.div animate={{
            y: [0, -5, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0
          }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-755" />
              <motion.div animate={{
            y: [0, -5, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0.2
          }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-756" />
              <motion.div animate={{
            y: [0, -5, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0.4
          }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-757" />
            </div>
          </motion.div>}
        <div ref={messagesEndRef} data-id="element-758" />
      </div>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-3 pb-safe z-40" data-id="element-759">
        <div className="max-w-md mx-auto flex items-end gap-2" data-id="element-760">
          <button className="p-2.5 text-gray-400 hover:text-primary transition-colors shrink-0" data-id="element-761">
            <Paperclip className="w-5 h-5" data-id="element-762" />
          </button>
          <div className="flex-1 bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden flex items-end" data-id="element-763">
            <textarea value={inputText} onChange={e => setInputText(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }} placeholder="Type a message..." className="w-full bg-transparent px-4 py-3 text-sm text-gray-900 focus:outline-none resize-none max-h-32 min-h-[44px]" rows={1} style={{
            height: 'auto'
          }} data-id="element-764" />
          </div>
          <button onClick={handleSend} disabled={!inputText.trim()} className={`p-3 rounded-full shrink-0 transition-all ${inputText.trim() ? 'bg-primary text-white shadow-md hover:bg-primary-600 active:scale-95' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-765">
            <Send className="w-5 h-5 ml-0.5" data-id="element-766" />
          </button>
        </div>
      </div>
    </motion.div>;
}